import { skipToken, useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { agentsApi, type AgentDatasetCollectionOptions, type AgentDatasetSelector, type AgentDatasetSummary } from '../../api/agents-api'
import { agentKeys } from '../../api/query-keys'
import { useAgentOperationDetail } from '../../lib/agent-operation-queries'

const collectionVisibilityAttempts = 10

const collectionVisibilityDelayMs = 3000

type DatasetQueryData = {
  pages: Array<{ items: AgentDatasetSummary[] }>
}

export function useCanonicalAssetDatasets(entityId: string | null, assetId: string | null) {
  return useQuery({
    queryKey: agentKeys.canonicalAssetDatasets(entityId ?? '', assetId ?? ''),
    queryFn: entityId === null || assetId === null
      ? skipToken
      : () => agentsApi.getCanonicalAssetDatasets(entityId, assetId),
  })
}

export function useCanonicalAssetDatasetSnapshot(
  entityId: string | null,
  assetId: string | null,
  selector: (AgentDatasetSelector & { agentId: string }) | null,
) {
  return useQuery({
    queryKey: agentKeys.canonicalAssetDatasetSnapshot(
      entityId ?? '',
      assetId ?? '',
      selector ?? { ...emptySelector, agentId: '' },
    ),
    queryFn: entityId === null || assetId === null || selector === null
      ? skipToken
      : () => agentsApi.getCanonicalAssetDatasetSnapshot(entityId, assetId, selector),
  })
}

export function useAgentDatasets(entityId: string | null, agentId: string | null) {
  return useInfiniteQuery({
    queryKey: agentKeys.datasets(entityId ?? '', agentId ?? ''),
    queryFn: entityId === null || agentId === null
      ? skipToken
      : ({ pageParam }) => agentsApi.getAgentDatasets(entityId, agentId, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.pageInfo.hasNextPage
      ? (lastPage.pageInfo.endCursor ?? undefined)
      : undefined,
  })
}

export function useCollectAgentDataset(entityId: string, agentId: string) {
  const queryClient = useQueryClient()
  const request = useMutation({
    mutationFn: (datasetType: string) =>
      agentsApi.collectAgentDataset(entityId, agentId, datasetType),
    onMutate: (datasetType) => ({
      datasetType,
      baseline: collectionBaseline(queryClient, entityId, agentId, datasetType),
    }),
  })
  const operationId = request.data?.operationId ?? null
  const operation = useAgentOperationDetail(
    entityId || null,
    agentId || null,
    operationId,
  )
  const visibility = useQuery({
    queryKey: agentKeys.collectionVisibility(entityId, agentId, operationId ?? ''),
    queryFn: async () => {
      if (request.context === undefined) throw new Error('Collection context is missing')

      await Promise.all([
        queryClient.refetchQueries({
          queryKey: agentKeys.datasets(entityId, agentId),
          type: 'active',
        }),
        queryClient.refetchQueries({
          queryKey: agentKeys.datasetCollectionOptions(entityId, agentId),
          type: 'active',
        }),
      ])
      const current = collectionBaseline(
        queryClient,
        entityId,
        agentId,
        request.context.datasetType,
      )
      const collectionObserved =
        current.datasetFingerprint !== request.context.baseline.datasetFingerprint
        || current.statusRevision !== request.context.baseline.statusRevision
      if (!collectionObserved) {
        throw new Error('Collected dataset is not visible')
      }
      await queryClient.invalidateQueries(
        { queryKey: agentKeys.datasetViews(entityId, agentId) },
        { throwOnError: true },
      )
      return true
    },
    enabled:
      operation.data?.operation.status === 'completed'
      && request.context !== undefined,
    retry: collectionVisibilityAttempts - 1,
    retryDelay: collectionVisibilityDelayMs,
    staleTime: Number.POSITIVE_INFINITY,
  })

  return { request, operation, visibility }
}

export function useAgentDatasetCollectionOptions(
  entityId: string,
  agentId: string,
  enabled: boolean,
) {
  return useQuery({
    queryKey: agentKeys.datasetCollectionOptions(entityId, agentId),
    queryFn: () => agentsApi.getAgentDatasetCollectionOptions(entityId, agentId),
    enabled,
  })
}

export function useAgentDatasetSnapshot(
  entityId: string | null,
  agentId: string | null,
  selector: AgentDatasetSelector | null,
) {
  return useQuery({
    queryKey: agentKeys.datasetView(entityId ?? '', agentId ?? '', 'snapshot', selector ?? emptySelector),
    queryFn: entityId === null || agentId === null || selector === null
      ? skipToken
      : () => agentsApi.getAgentDatasetSnapshot(entityId, agentId, selector),
    gcTime: 0,
  })
}

export function useAgentDatasetRevisions(
  entityId: string | null,
  agentId: string | null,
  selector: AgentDatasetSelector | null,
) {
  return useInfiniteQuery({
    queryKey: agentKeys.datasetView(entityId ?? '', agentId ?? '', 'revisions', selector ?? emptySelector),
    queryFn: entityId === null || agentId === null || selector === null
      ? skipToken
      : ({ pageParam }) => agentsApi.getAgentDatasetRevisions(entityId, agentId, selector, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.pageInfo.hasNextPage
      ? (lastPage.pageInfo.endCursor ?? undefined)
      : undefined,
  })
}

export function useAgentDatasetRevision(
  entityId: string | null,
  agentId: string | null,
  selector: AgentDatasetSelector | null,
  epochId: string | null,
  revision: string | null,
) {
  return useQuery({
    queryKey: agentKeys.datasetRevision(
      entityId ?? '',
      agentId ?? '',
      selector ?? emptySelector,
      epochId ?? '',
      revision ?? '',
    ),
    queryFn:
      entityId === null
      || agentId === null
      || selector === null
      || epochId === null
      || revision === null
        ? skipToken
        : () => agentsApi.getAgentDatasetRevision(
            entityId,
            agentId,
            selector,
            epochId,
            revision,
          ),
    gcTime: 0,
  })
}

export function useAgentDatasetReconciliation(
  entityId: string | null,
  agentId: string | null,
  selector: AgentDatasetSelector | null,
) {
  return useInfiniteQuery({
    queryKey: agentKeys.datasetView(entityId ?? '', agentId ?? '', 'reconciliation', selector ?? emptySelector),
    queryFn: entityId === null || agentId === null || selector === null
      ? skipToken
      : ({ pageParam }) => agentsApi.getAgentDatasetReconciliation(entityId, agentId, selector, pageParam || undefined),
    initialPageParam: '',
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  })
}

const emptySelector: AgentDatasetSelector = {
  localSubjectId: '',
  datasetType: '',
  instanceKey: '',
}

function collectionBaseline(
  queryClient: QueryClient,
  entityId: string,
  agentId: string,
  datasetType: string,
) {
  return {
    datasetFingerprint: datasetFingerprint(
      queryClient.getQueryData<DatasetQueryData>(agentKeys.datasets(entityId, agentId)),
      datasetType,
    ),
    statusRevision: queryClient.getQueryData<AgentDatasetCollectionOptions>(
      agentKeys.datasetCollectionOptions(entityId, agentId),
    )?.statusRevisions[datasetType] ?? null,
  }
}

function datasetFingerprint(data: DatasetQueryData | undefined, datasetType: string) {
  const versions = data?.pages
    .flatMap((page) => page.items)
    .filter((dataset) => dataset.datasetType === datasetType)
    .map((dataset) => JSON.stringify([
      dataset.localSubjectId,
      dataset.instanceKey,
      dataset.epochId,
      dataset.revision,
      dataset.receivedAt,
    ]))
    .sort() ?? []
  return JSON.stringify(versions)
}
